import { motion } from 'framer-motion';
import { Check, X, AlertCircle, Gift, Percent, Coffee, ArrowUpCircle, Calendar } from 'lucide-react';

const comparisonData = [
  {
    feature: 'Room Rate',
    direct: { value: '₹8,500', highlight: true, icon: Percent },
    ota: { value: '₹9,200', highlight: false },
    savings: '₹700'
  },
  {
    feature: 'Free Breakfast',
    direct: { value: 'Included', included: true, icon: Coffee },
    ota: { value: 'Extra ₹500', included: false },
    savings: '₹500'
  },
  {
    feature: 'Welcome Drink',
    direct: { value: 'Complimentary', included: true, icon: Gift },
    ota: { value: 'Not included', included: false },
    savings: '₹200'
  },
  {
    feature: 'Room Upgrade',
    direct: { value: 'Subject to availability', included: true, icon: ArrowUpCircle },
    ota: { value: 'Not available', included: false },
    savings: '-'
  },
  {
    feature: 'Cancellation',
    direct: { value: 'Free up to 24hrs', included: true, icon: Calendar },
    ota: { value: 'Non-refundable', included: false, warning: true },
    savings: '-'
  },
];

export function BookingComparisonTable({ className = '' }) {
  const totalSavings = 1400; // ₹700 + ₹500 + ₹200

  return (
    <div className={`bg-card border border-border rounded-xl overflow-hidden ${className}`} data-testid="booking-comparison-table">
      {/* Header */}
      <div className="bg-emerald-800 text-white px-4 sm:px-6 py-3 sm:py-4 text-center">
        <h3 className="font-serif text-lg sm:text-xl mb-1">Why Book Direct?</h3>
        <p className="text-emerald-200 text-xs sm:text-sm">Save up to ₹{totalSavings.toLocaleString()} per night</p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs sm:text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-left font-medium text-muted-foreground">Feature</th>
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium">
                <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                  <Gift className="w-3 h-3 sm:w-4 sm:h-4 hidden sm:inline" />
                  <span className="hidden sm:inline">Direct Booking</span>
                  <span className="sm:hidden">Direct</span>
                </span>
              </th>
              <th className="px-2 sm:px-4 py-2 sm:py-3 text-center font-medium text-muted-foreground">
                <span className="hidden sm:inline">OTA (Booking.com)</span>
                <span className="sm:hidden">OTA</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {comparisonData.map((row, index) => (
              <tr key={index} className="border-b border-border last:border-0">
                <td className="px-2 sm:px-4 py-2 sm:py-3 text-foreground font-medium">{row.feature}</td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                  <span className={`inline-flex items-center gap-1 ${row.direct.highlight ? 'text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-foreground'}`}>
                    {row.direct.included && <Check className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-500" />}
                    <span className="hidden sm:inline">{row.direct.value}</span>
                    <span className="sm:hidden">{row.direct.value.split(' ')[0]}</span>
                  </span>
                </td>
                <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                  <span className={`inline-flex items-center gap-1 ${row.ota.warning ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {row.ota.included === false && !row.ota.warning && <X className="w-3 h-3 sm:w-4 sm:h-4 text-red-400" />}
                    {row.ota.warning && <AlertCircle className="w-3 h-3 sm:w-4 sm:h-4" />}
                    <span className="hidden sm:inline">{row.ota.value}</span>
                    <span className="sm:hidden">{row.ota.value.split(' ')[0]}</span>
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* CTA */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-emerald-50 dark:bg-emerald-950/50 text-center">
        <a
          href="/book"
          className="btn-emerald inline-flex items-center gap-2 text-xs sm:text-sm py-2 sm:py-3 px-4 sm:px-6"
          data-testid="comparison-book-direct-btn"
        >
          Book Direct & Save
        </a>
      </div>
    </div>
  );
}

export function BookDirectBadge({ variant = 'default', className = '' }) {
  if (variant === 'corner') {
    return (
      <div className={`absolute top-3 left-3 z-10 ${className}`} data-testid="book-direct-badge-corner">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-600 text-white text-xs font-medium rounded-full shadow-lg">
          <Percent className="w-3 h-3" />
          Best Price
        </span>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 text-xs font-medium rounded ${className}`} data-testid="book-direct-badge-inline">
        <Check className="w-3 h-3" />
        Best Rate Guarantee
      </span>
    );
  }

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-600 to-emerald-500 text-white text-sm font-medium rounded-full shadow ${className}`} data-testid="book-direct-badge">
      <Gift className="w-4 h-4" />
      Book Direct & Get Free Breakfast
    </div>
  );
}

export function BookDirectBanner({ onClose }) {
  return (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      className="fixed top-0 left-0 right-0 z-40 bg-gradient-to-r from-emerald-700 via-emerald-600 to-emerald-700 text-white py-2.5 px-4 shadow-lg"
      data-testid="book-direct-banner"
    >
      <div className="container-custom flex items-center justify-center gap-4 text-sm">
        <span className="flex items-center gap-2">
          <Gift className="w-4 h-4" />
          <span className="hidden sm:inline">Book Direct &</span> Save 10% + Free Breakfast
        </span>
        <a
          href="/book"
          className="px-3 py-1 bg-white text-emerald-700 rounded-full text-xs font-semibold hover:bg-emerald-50 transition-colors"
        >
          Book Now
        </a>
        {onClose && (
          <button onClick={onClose} className="ml-2 p-1 hover:bg-emerald-600 rounded">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </motion.div>
  );
}

export function DirectBookingBenefits({ compact = false }) {
  const benefits = [
    { icon: Percent, title: '10% Lower Rates', desc: 'Best price guaranteed' },
    { icon: Coffee, title: 'Free Breakfast', desc: 'Daily breakfast included' },
    { icon: Gift, title: 'Welcome Drink', desc: 'Complimentary on arrival' },
    { icon: ArrowUpCircle, title: 'Room Upgrade', desc: 'Subject to availability' },
  ];

  if (compact) {
    return (
      <div className="flex flex-wrap gap-3" data-testid="direct-booking-benefits-compact">
        {benefits.map((benefit, index) => (
          <span key={index} className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
            <benefit.icon className="w-3.5 h-3.5" />
            {benefit.title}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="direct-booking-benefits">
      {benefits.map((benefit, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="text-center p-4 bg-card border border-border rounded-lg"
        >
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-emerald-100 dark:bg-emerald-900 flex items-center justify-center">
            <benefit.icon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h4 className="font-medium text-sm text-foreground">{benefit.title}</h4>
          <p className="text-xs text-muted-foreground mt-0.5">{benefit.desc}</p>
        </motion.div>
      ))}
    </div>
  );
}
